import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { THEME_COLORS, THEME_RADIUS } from '../constants/theme';

const PaymentFailed = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tripId = searchParams.get('trip_id');
  const error = searchParams.get('error') || 'Payment could not be processed';

  const handleRetry = () => {
    if (tripId) {
      navigate(`/trips/${tripId}`);
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  // Common error messages and their user-friendly versions
  const getErrorMessage = (error) => {
    const errorMessages = {
      'card_declined': 'Your card was declined. Please try a different card or contact your bank.',
      'insufficient_funds': 'Insufficient funds. Please check your account balance or try a different card.',
      'expired_card': 'Your card has expired. Please use a different card.',
      'incorrect_cvc': 'The security code is incorrect. Please check your card and try again.',
      'processing_error': 'There was a processing error. Please try again in a few moments.',
      'authentication_required': 'Additional authentication is required. Please try again.',
      'generic_decline': 'Your payment was declined. Please try a different payment method.',
      'default': 'We were unable to process your payment. Please try again or use a different payment method.'
    };

    return errorMessages[error] || errorMessages['default'];
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card sx={{ 
        borderRadius: THEME_RADIUS.large,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#ffebee',
          color: '#c62828',
          p: 4,
          textAlign: 'center'
        }}>
          <ErrorIcon sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Payment Failed
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            We couldn't process your payment
          </Typography>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: THEME_RADIUS.small }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              Payment Error
            </Typography>
            <Typography variant="body2">
              {getErrorMessage(error)}
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, textAlign: 'center' }}>
            Don't worry! No charges were made to your account. You can try again with the same or a different payment method.
          </Typography>

          {/* Common solutions */}
          <Box sx={{ 
            bgcolor: '#f8f9fa', 
            p: 2, 
            borderRadius: THEME_RADIUS.small,
            mb: 3
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Suggestions:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>Check that your card details are correct</li>
              <li>Ensure you have sufficient funds</li>
              <li>Try a different payment method</li>
              <li>Contact your bank if the problem persists</li>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleGoHome}
              startIcon={<ArrowBackIcon />}
              sx={{
                py: 1.5,
                borderRadius: THEME_RADIUS.medium,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Back to Home
            </Button>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
              sx={{
                backgroundColor: THEME_COLORS.primary.main,
                py: 1.5,
                borderRadius: THEME_RADIUS.medium,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.dark
                }
              }}
            >
              Try Again
            </Button>
          </Box>

          {/* Help section */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <CreditCardIcon sx={{ fontSize: 32, color: THEME_COLORS.primary.main, mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Need help with your payment?
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/support')}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Contact Support
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentFailed;
